import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { FaArrowLeft, FaCalendarAlt, FaChartLine, FaUsers } from "react-icons/fa";
import { MdEmail, MdLocationOn, MdPhone } from "react-icons/md";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../api";

const currency = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 2,
});

const formatCurrency = (value) => currency.format(Number(value) || 0);

export default function SchoolProfile() {
  const { schoolId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [school, setSchool] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [adminCount, setAdminCount] = useState(0);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    const fetchProfile = async () => {
      try {
        const [schoolsResponse, subscriptionsResponse, adminsResponse] = await Promise.all([
          api.get("/super_admin/school/list"),
          api.get(`/super_admin/subscriptions/list?school_id=${schoolId}`),
          api.get(`/super_admin/school_admin/list?school_id=${schoolId}`),
        ]);

        if (!active) return;
        const selectedSchool = (schoolsResponse.data?.data || []).find(
          (item) => String(item.id) === String(schoolId)
        );
        setSchool(selectedSchool || null);
        setSubscriptions(subscriptionsResponse.data?.data || []);
        setAdminCount((adminsResponse.data?.data || []).length);
      } catch (requestError) {
        console.error("Fetch school profile error:", requestError);
        if (active) setError("Unable to load school profile.");
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchProfile();
    return () => {
      active = false;
    };
  }, [schoolId]);

  const stats = useMemo(() => {
    const paidSubscriptions = subscriptions.filter(
      (subscription) => String(subscription.status).toLowerCase() === "paid"
    );
    const monthlyRevenue = paidSubscriptions.reduce(
      (sum, subscription) => sum + (Number(subscription.amount) || 0),
      0
    );
    const latestSubscription = [...subscriptions].sort((first, second) =>
      String(second.due_date || "").localeCompare(String(first.due_date || ""))
    )[0];
    const dueDate = latestSubscription?.due_date || "";
    const dueDateParts = dueDate.split("-").map(Number);
    const dueDateAtMidnight = dueDateParts.length === 3 && dueDateParts.every(Number.isFinite)
      ? new Date(dueDateParts[0], dueDateParts[1] - 1, dueDateParts[2])
      : null;
    const today = new Date();
    const todayAtMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const remainingDays = dueDateAtMidnight
      ? Math.ceil((dueDateAtMidnight.getTime() - todayAtMidnight.getTime()) / (1000 * 60 * 60 * 24))
      : null;

    return {
      monthlyRevenue,
      totalRevenue: monthlyRevenue,
      subscriptionCount: subscriptions.length,
      paidSubscriptionCount: paidSubscriptions.length,
      dueDate: dueDate || "-",
      remainingDays,
      subscriptionStatus: remainingDays === null
        ? "No subscription"
        : remainingDays >= 0
        ? "Subscription active"
        : "Subscription inactive",
    };
  }, [school, subscriptions]);

  if (loading) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}><CircularProgress sx={{ color: "#0891b2" }} /></Box>;
  }

  if (!school) {
    return <Alert severity="error">School profile not found.</Alert>;
  }

  const infoItems = [
    { label: "Address", value: school.address || "Not available", icon: <MdLocationOn /> },
    { label: "Phone", value: school.phone || "Not available", icon: <MdPhone /> },
    { label: "Email", value: school.email || "Not available", icon: <MdEmail /> },
    { label: "Domain", value: school.domain || "Not available", icon: <FaChartLine /> },
    { label: "Created At", value: school.created_at || "Not available", icon: <FaCalendarAlt /> },
  ];

  const metricItems = [
    { label: "Monthly Revenue", value: formatCurrency(stats.monthlyRevenue), icon: <FaCalendarAlt />, color: "#0f766e" },
    { label: "Total Revenue", value: formatCurrency(stats.totalRevenue), icon: <FaChartLine />, color: "#f59e0b" },
    { label: "Admin & Staff", value: adminCount, icon: <FaUsers />, color: "#16a34a" },
  ];

  return (
    <Box sx={{ minHeight: "100vh" }}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Card sx={{ p: { xs: 1.5, md: 2.5 }, borderRadius: 2, border: "1px solid rgba(78,102,135,0.14)", boxShadow: "0 18px 46px rgba(16,42,67,0.12)", background: "rgba(255,255,255,0.78)" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, mb: 2.5, p: { xs: 1.5, md: 2 }, borderRadius: 2, background: "linear-gradient(120deg, #102a43 0%, #123a5a 58%, #0f766e 100%)", boxShadow: "0 14px 30px rgba(16,42,67,0.2)" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
            {school.logo ? (
              <Box component="img" src={`https://dgps.co.in/public/admin/assets/img/schools/logo/${school.logo}`} alt="School logo" sx={{ width: 62, height: 62, objectFit: "contain", borderRadius: 1.5, border: "2px solid rgba(255,255,255,0.72)", background: "#fff" }} />
            ) : (
              <Box sx={{ width: 62, height: 62, display: "grid", placeItems: "center", borderRadius: 1.5, color: "#fff", fontSize: 25, fontWeight: 900, background: "rgba(255,255,255,0.16)", border: "2px solid rgba(255,255,255,0.72)" }}>{school.name?.charAt(0)}</Box>
            )}
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ color: "#fff", fontWeight: 900, fontSize: { xs: 21, md: 28 }, overflowWrap: "anywhere" }}>{school.name}</Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.74)", fontWeight: 700, mt: 0.35 }}>School profile &nbsp;|&nbsp; Code: {school.code || "-"}</Typography>
            </Box>
          </Box>
          <Button variant="outlined" startIcon={<FaArrowLeft />} onClick={() => navigate("/super_admin/schools")} sx={{ flexShrink: 0, borderRadius: 1.5, textTransform: "none", fontWeight: 800, color: "#fff", borderColor: "rgba(255,255,255,0.52)", "&:hover": { borderColor: "#fff", background: "rgba(255,255,255,0.1)" } }}>Back</Button>
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }, gap: 2, mb: 3 }}>
          {metricItems.map((metric) => (
            <Paper key={metric.label} sx={{ p: 2, display: "flex", alignItems: "center", gap: 1.25, borderRadius: 2, border: "1px solid rgba(78,102,135,0.14)", borderTop: `4px solid ${metric.color}`, boxShadow: "0 10px 24px rgba(16,42,67,0.08)", background: "#fff" }}>
              <Box sx={{ width: 42, height: 42, display: "grid", placeItems: "center", borderRadius: 1.25, color: metric.color, background: `${metric.color}18`, fontSize: 19 }}>{metric.icon}</Box>
              <Box><Typography sx={{ color: "#607086", fontSize: 12, fontWeight: 700 }}>{metric.label}</Typography><Typography sx={{ color: "#172033", fontSize: 19, fontWeight: 900 }}>{metric.value}</Typography></Box>
            </Paper>
          ))}
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.1fr 0.9fr" }, gap: 2, mb: 3 }}>
          <Paper sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 2, border: "1px solid rgba(78,102,135,0.14)", boxShadow: "0 8px 22px rgba(16,42,67,0.05)", background: "#fff" }}>
            <Typography sx={{ color: "#102a43", fontSize: 18, fontWeight: 900, mb: 2 }}>School Information</Typography>
            <Box sx={{ display: "grid", gap: 1.5 }}>
              {infoItems.map((item) => <Box key={item.label} sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}><Box sx={{ color: "#2563eb", fontSize: 19, mt: 0.15 }}>{item.icon}</Box><Box><Typography sx={{ color: "#607086", fontSize: 12, fontWeight: 700 }}>{item.label}</Typography><Typography sx={{ color: "#172033", fontWeight: 700, overflowWrap: "anywhere" }}>{item.value}</Typography></Box></Box>)}
            </Box>
          </Paper>
          <Paper sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 2, border: "1px solid rgba(78,102,135,0.14)", boxShadow: "0 8px 22px rgba(16,42,67,0.05)", background: "#fff" }}>
            <Typography sx={{ color: "#102a43", fontSize: 18, fontWeight: 900, mb: 2 }}>Subscription Summary</Typography>
            <Box sx={{ display: "grid", gap: 1.5 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}><Typography color="text.secondary">Total subscriptions</Typography><Typography fontWeight={900}>{stats.subscriptionCount}</Typography></Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}><Typography color="text.secondary">Paid subscriptions</Typography><Typography fontWeight={900}>{stats.paidSubscriptionCount}</Typography></Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}><Typography color="text.secondary">Latest due date</Typography><Typography fontWeight={900}>{stats.dueDate}</Typography></Box>
              <Chip
                label={stats.subscriptionStatus}
                color={stats.remainingDays === null ? "warning" : stats.remainingDays >= 0 ? "success" : "error"}
                sx={{ alignSelf: "flex-start", fontWeight: 800 }}
              />
            </Box>
          </Paper>
        </Box>

        <Paper sx={{ mt: 3, p: { xs: 1.5, md: 2.5 }, borderRadius: 2, border: "1px solid rgba(78,102,135,0.14)", boxShadow: "0 10px 26px rgba(16,42,67,0.06)", background: "#fff" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ width: 30, height: 30, display: "grid", placeItems: "center", borderRadius: 1, color: "#0f766e", background: "#e5f5f2", fontSize: 14, fontWeight: 900 }}>
                ₹
              </Box>
              <Typography sx={{ color: "#102a43", fontSize: 18, fontWeight: 900 }}>
                Monthly Transactions
              </Typography>
            </Box>
            <Typography sx={{ color: "#607086", fontSize: 13, fontWeight: 700 }}>
              {subscriptions.length} {subscriptions.length === 1 ? "record" : "records"}
            </Typography>
          </Box>
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 760 }}>
              <TableHead sx={{ bgcolor: "#123a5a" }}>
                <TableRow>
                  {["Month", "Amount", "Due Date", "Paid Date", "Status", "Mode", "Transaction ID"].map((heading) => (
                    <TableCell key={heading} sx={{ color: "#fff", fontWeight: 800, whiteSpace: "nowrap", borderBottom: 0 }}>
                      {heading}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {subscriptions.map((subscription, index) => {
                  const status = String(subscription.status || "pending").toLowerCase();
                  return (
                    <TableRow key={subscription.id || `${subscription.month}-${index}`} hover sx={{ backgroundColor: index % 2 ? "#f7fafc" : "#fff", "& td": { borderColor: "rgba(78,102,135,0.12)" } }}>
                      <TableCell sx={{ fontWeight: 800, color: "#172033" }}>{subscription.month || "-"}</TableCell>
                      <TableCell sx={{ fontWeight: 800 }}>{formatCurrency(subscription.amount)}</TableCell>
                      <TableCell>{subscription.due_date || "-"}</TableCell>
                      <TableCell>{subscription.paid_date || "-"}</TableCell>
                      <TableCell>
                        <Chip
                          label={status.toUpperCase()}
                          size="small"
                          color={status === "paid" ? "success" : status === "pending" ? "warning" : "error"}
                          sx={{ fontWeight: 800 }}
                        />
                      </TableCell>
                      <TableCell>{subscription.payment_mode || "-"}</TableCell>
                      <TableCell>{subscription.transaction_id || "-"}</TableCell>
                    </TableRow>
                  );
                })}
                {!subscriptions.length && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 5, color: "#607086", fontWeight: 700 }}>
                      No monthly transactions found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Card>
    </Box>
  );
}
